/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED } from "../../../../share/util/api/polling";

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: 'standby',
  START: 'start',
  EXECUTION: 'execution',
  LEFT_LAMP_ON: 'leftLampOn',
  RIGHT_LAMP_ON: 'rightLampOn',
  FAILED: 'failed',
};

export default {
  name: 'requirement092',
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      systemId: '',
      modal_requirement_092: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.LEFT_LAMP_ON:
        case PHASE.RIGHT_LAMP_ON:
          return 'success';
        case PHASE.STANDBY:
        case PHASE.START:
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 待機
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行、実行中(RH側)、実行中(LH側)
      const ExecutionTitle = 'individual.common_title_execution';
      // 停止
      const LampOnTitle = 'message.common_title_success';
      // 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ConfirmationTitle,
        execution: ExecutionTitle,
        leftLampOn: LampOnTitle,
        rightLampOn: LampOnTitle,
        failed: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_92_TXT_1`,
        execution: `${this.i18nWsKey}.REQ_WS_92_TXT_2`,
        leftLampOn: `${this.i18nWsKey}.REQ_WS_92_TXT_3_LH`,
        rightLampOn: `${this.i18nWsKey}.REQ_WS_92_TXT_3_RH`,
        failed: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_092 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_092 = false;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 初期処理(NonDefaultSessionに遷移する) */
    async standby() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // 初期処理 (要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：初期化開始確認画面表示
              this.phase = PHASE.START;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 初期画面の「OK」ボタン押下 */
    async onStartOkClicked() {
      // 実行画面へ遷移する
      this.phase = PHASE.EXECUTION;
    },

    /** キャンセル押下処理 */
    async onCancelClicked() {
      switch (this.phase) {
        case PHASE.LEFT_LAMP_ON:
          await this.stop(false);
          break;
        case PHASE.RIGHT_LAMP_ON:
          await this.stop(true);
          break;
      }
    },

    /**
     * 実行処理（要求）
     * @param {boolean} isRH LH,RH判定 
     */
    async execute(isRH) {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
            lamp: isRH ? 'rh' : 'lh'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在しない場合、エラーに遷移
        if (response) {
          // ルーチン実行結果確認
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.phase = isRH ? PHASE.RIGHT_LAMP_ON : PHASE.LEFT_LAMP_ON;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 停止画面の「キャンセル」ボタン押下
     * @param {boolean} isRH LH,RH判定 
     */
    async stop(isRH) {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop',
            lamp: isRH ? 'rh' : 'lh'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在しない場合、エラーに遷移
        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              await this.exitProcess();
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exitProcess() {
      // DefaultSession遷移
      await this.exit();
    },

    /** Exit処理(要求) */
    async exit() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在しない場合、エラーに遷移
        if (response) {
          if (this.phase === PHASE.FAILED) {
            this.closeWizard();
            return;
          }
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.closeWizard();
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        } else {
          this.showUnexpectedError();
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          await this.onStartOkClicked();
          break;
        case PHASE.FAILED:
          await this.exitProcess();
          break;
        default:
          break;
      }
    },

    async onLhClicked() {
      await this.execute(false);
    },

    async onRhClicked() {
      await this.execute(true);
    },
  },

  async mounted() {
    this.init();
    // 初期処理
    await this.standby();
  },
};